import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomModal from "../../Common/CustomModal";
import Select from "react-select";
import "./style.scss";
import { useAppContext } from "../../Context/AppContext";
import { getComuneLayers, getLayer } from "../../../api";
import { ExclamationTriangleIcon } from "../../../assets/icons";
import validateProvenienza from "../../../helpers/formTools/validations/provenienza/validateProvenienza";
import { getReactSelectThemeProps } from "../../../helpers/reactSelectThemeStyles";

const reactSelectThemeProps = getReactSelectThemeProps();

const ProvenienzaAddModal = ({
  show,
  provenienze,
  handleClose,
  handleAddProvenienza,
}) => {
  const navigate = useNavigate();
  const { comuni } = useAppContext();
  const [selectedComune, setSelectedComune] = useState(null);
  const [layers, setLayers] = useState([]);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [layerFeatures, setLayerFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedValue, setSelectedValue] = useState(null);
  const [issues, setIssues] = useState([]);

  const layersOptions = useMemo(
    () =>
      layers
        .filter((l) => l.tipo !== "limiti")
        .map((item) => ({
          value: item.id,
          label: item.tipo,
          chiave: item.chiave,
        })),
    [layers]
  );

  const featuresOptions = useMemo(
    () =>
      layerFeatures
        .map((item) => ({
          value:  item.properties[selectedLayer?.chiave] || "N/A",
          label: item.properties[selectedLayer?.chiave] || "N/A"
        })),
    [layerFeatures]
  );


  const isDisableSelect = useMemo(() => provenienze.length > 0, [provenienze]);

  const listComuneLayers = useCallback(
    async (comune) => {
      try {
        const data = await getComuneLayers(comune.value);
        setLayers(data.layers);
        return data.layers;
      } catch (error) {
        if (error?.response?.status === 401) {
          navigate("/");
          console.error(`Authorization error listing layers`);
        } else {
          toast.error(
            error.response && error.response.detail
              ? error.response.detail
              : `Unknown error listing layers`
          );
          console.error(`Authorization error listing layers`, error);
        }
      }
    },
    [navigate]
  );

  const listLayerFeatures = useCallback(
    async (layer) => {
      try {
        const data = await getLayer(layer.value);
        setLayerFeatures(data.features);
        return data.features;
      } catch (error) {
        if (error?.response?.status === 401) {
          navigate("/");
          console.error(`Authorization error listing layers`);
        } else {
          toast.error(
            error.response && error.response.detail
              ? error.response.detail
              : `Unknown error listing layers`
          );
          console.error(`Authorization error listing features`, error);
        }
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (selectedComune) listComuneLayers(selectedComune);
  }, [selectedComune, listComuneLayers]);

  useEffect(() => {
    if (selectedLayer) listLayerFeatures(selectedLayer);
  }, [selectedLayer, listLayerFeatures]);

  useEffect(() => {
    if (provenienze.length === 0) return;
    const comune = comuni.find((c) => c?.id === provenienze[0].comuneId);
    setSelectedComune({ value: comune?.id, label: comune?.nome });
  }, [provenienze, comuni]);

  // useEffect(() => {
  //   if (layers.length === 0 || provenienze.length === 0) return;
  //   const layer = layers.find((l) => l.tipo === provenienze[0].tipo);
  //   setSelectedLayer({
  //     value: layer?.id,
  //     label: layer.tipo,
  //     chiave: layer.chiave,
  //   });
  // }, [layers, provenienze]);

  /* ALL HANDLERS */

  const onSelectedComune = (comune) => {
    setSelectedComune(comune);
  };

  const onSelectedLayer = (layer) => {
    setSelectedLayer(layer);
  };

  const onSelectedFeature = (feature) => {
    setSelectedFeature(feature);
  };

  const onSelectedValue = (e) => {
    setSelectedValue(e.target.value);
  };
  /* TRANSFORMATIONS */

  const comuniOptions = comuni.map((item) => ({
    value: `${item.id}`,
    label: `${item.nome}`,
  }));

  const onAddProvenienza = (e) => {
    e.preventDefault();
    const provenienza = {
      comuneId: selectedComune.value,
      tipo: selectedLayer.label,
      chiave: selectedLayer.chiave,
      valore: selectedFeature.value,
    };
    const issues = validateProvenienza(provenienza);
    if (issues.length > 0) {
      setIssues(issues);
      return;
    }
    handleAddProvenienza(provenienza);
  };


  return (
    <CustomModal
      title={"AGGIUNGI PROVENIENZA"}
      isVisible={show}
      onClose={handleClose}
    >
      <div className="issue-container">
        {issues.length > 0 &&
          issues.map((issue, index) => {
            return (
              <div key={index} className="issue">
                <p className="issue-icon">
                  <ExclamationTriangleIcon />{" "}
                </p>
                {issue}
              </div>
            );
          })}
      </div>
      <form onSubmit={onAddProvenienza}>
        <div className="add-user-modal-body">
          <div className="input-wrapper">
            <label htmlFor="comune" className="input-label">
              Comune, Frazione o Parco:
            </label>
            <Select
              options={comuniOptions}
              required
              value={selectedComune}
              isDisabled={isDisableSelect}
              placeholder="Seleziona una opzione"
              onChange={onSelectedComune}
              {...reactSelectThemeProps}
            ></Select>
          </div>
          <div className="input-wrapper">
            <label htmlFor="layer" className="input-label">
              Layer:
            </label>
            <Select
              options={layersOptions}
              required
              value={selectedLayer}
              placeholder="Seleziona una opzione"
              onChange={onSelectedLayer}
              {...reactSelectThemeProps}
            ></Select>
          </div>
          <div className="input-wrapper">
            <label htmlFor="value" className="input-label">
              {selectedLayer ? selectedLayer.chiave : "..."}
            </label>
            <Select
              options={featuresOptions.sort((a, b) => a.label.localeCompare(b.label))}
              required
              value={selectedFeature}
              placeholder="Seleziona una opzione"
              onChange={onSelectedFeature}
              {...reactSelectThemeProps}
            ></Select>
          </div>
        </div>
        <footer className="modal-footer">
          <button type="submit" className="modal-footer-button">
            Aggiungi
          </button>
        </footer>
      </form>
    </CustomModal>
  );
};

export default ProvenienzaAddModal;
